import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Button, Input, Textarea, Tag } from "../../../../design-system";
import {
  useMarketingContext,
  saveMarketingContext,
} from "../../../../database/marketing/useMarketingContext";
import {
  ContentAngle,
  Competitor,
  CustomerSegment,
  IdentityAndProduct,
  MarketingContext,
  PurposeAndPositioning,
} from "../../../../types/MarketingContext";

type Props = {
  communityId?: string;
  isActive?: boolean;
};

const MAX_TONE = 10;

/* ------------------------------- styles -------------------------------- */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-5);
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-5);
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const PanelSubtitle = styled.p`
  margin: 0 0 var(--space-4);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const FieldLabel = styled.div`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-1);
`;

const FieldValue = styled.p`
  margin: 0 0 var(--space-4);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text);
  white-space: pre-wrap;
`;

const OwnedSpaceBox = styled.div`
  margin-top: var(--space-1);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-warning-border, #f0c9a8);
  background: var(--color-warning-subtle, #fdf1e7);
  border-radius: var(--radius-md);
  color: var(--color-warning-text, #b5601f);
  font-size: var(--font-size-sm);
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  &:hover {
    color: var(--color-text);
    background: var(--color-surface-subtle);
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-3);
`;

const Counter = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
`;

const ToneCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-5);
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const SegBar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    #3b82f6 0%,
    #8b5cf6 45%,
    #ec4899 72%,
    #f59e0b 100%
  );
  margin-bottom: var(--space-3);
`;

const Muted = styled.p`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const AngleDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) 0 var(--space-1);
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-3);
`;

/* ------------------------------ helpers -------------------------------- */

const splitList = (s: string): string[] =>
  s
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);

const joinList = (arr?: string[]): string => (arr && arr.length ? arr.join(", ") : "");

/* ------------------------------ component ------------------------------ */

const BrandForm = ({ communityId }: Props) => {
  const { context, loading } = useMarketingContext({ communityId });

  // Local editable copies
  const [angles, setAngles] = useState<ContentAngle[]>([]);
  const [toneDos, setToneDos] = useState<string[]>([]);
  const [toneDonts, setToneDonts] = useState<string[]>([]);
  const [identity, setIdentity] = useState<IdentityAndProduct>({});
  const [purpose, setPurpose] = useState<PurposeAndPositioning>({});
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);

  // Input buffers
  const [newAngle, setNewAngle] = useState("");
  const [newDo, setNewDo] = useState("");
  const [newDont, setNewDont] = useState("");
  const [expandedAngle, setExpandedAngle] = useState<number | null>(null);
  const [editIdentity, setEditIdentity] = useState(false);
  const [editPurpose, setEditPurpose] = useState(false);
  const [editMarket, setEditMarket] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate local state whenever the doc changes. For communities saved before
  // the structured fields existed, fall back to the legacy flat fields the MCP
  // wrote (alternatives, doNotSay, positioning) so the tab is not blank. These
  // are display seeds; they persist into the structured shape on first save.
  useEffect(() => {
    if (!context) return;
    setAngles(context.contentAngles ?? []);
    setToneDos(context.toneDos ?? []);
    setToneDonts(context.toneDonts ?? context.doNotSay ?? []);
    setIdentity(
      context.identity ??
        (context.positioning ? { coreIdentity: context.positioning } : {})
    );
    setPurpose(context.purpose ?? {});
    setSegments(context.market?.customerSegments ?? []);
    setCompetitors(
      context.market?.competitors ??
        (context.alternatives ?? []).map((label) => ({ label }))
    );
  }, [context]);

  const persist = async (partial: Partial<MarketingContext>) => {
    if (!communityId) return;
    setSaving(true);
    try {
      await saveMarketingContext(communityId, partial);
    } finally {
      setSaving(false);
    }
  };

  /* --------- Content Angles --------- */
  const addAngle = () => {
    const name = newAngle.trim();
    if (!name) return;
    const next = [...angles, { name }];
    setAngles(next);
    setNewAngle("");
    persist({ contentAngles: next });
  };

  const updateAngle = (idx: number, patch: Partial<ContentAngle>) => {
    const next = angles.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    setAngles(next);
  };

  const commitAngles = () => persist({ contentAngles: angles });

  const deleteAngle = (idx: number) => {
    const next = angles.filter((_, i) => i !== idx);
    setAngles(next);
    if (expandedAngle === idx) setExpandedAngle(null);
    persist({ contentAngles: next });
  };

  /* --------- Tone --------- */
  const addTone = (kind: "do" | "dont") => {
    if (kind === "do") {
      const v = newDo.trim();
      if (!v || toneDos.length >= MAX_TONE) return;
      const next = [...toneDos, v];
      setToneDos(next);
      setNewDo("");
      persist({ toneDos: next });
    } else {
      const v = newDont.trim();
      if (!v || toneDonts.length >= MAX_TONE) return;
      const next = [...toneDonts, v];
      setToneDonts(next);
      setNewDont("");
      persist({ toneDonts: next });
    }
  };

  const removeTone = (kind: "do" | "dont", idx: number) => {
    if (kind === "do") {
      const next = toneDos.filter((_, i) => i !== idx);
      setToneDos(next);
      persist({ toneDos: next });
    } else {
      const next = toneDonts.filter((_, i) => i !== idx);
      setToneDonts(next);
      persist({ toneDonts: next });
    }
  };

  /* --------- Market --------- */
  const addSegment = () =>
    setSegments([...segments, { label: "", percent: undefined }]);
  const addCompetitor = () =>
    setCompetitors([...competitors, { label: "", url: "" }]);

  const saveMarket = () => {
    persist({
      market: {
        customerSegments: segments.filter((s) => s.label.trim()),
        competitors: competitors.filter((c) => c.label.trim()),
      },
    });
    setEditMarket(false);
  };

  if (!communityId) {
    return <Muted>Select a community to edit its brand.</Muted>;
  }
  if (loading) {
    return <Muted>Loading brand context…</Muted>;
  }

  return (
    <Wrap>
      {/* ---------------- Content Angles ---------------- */}
      <Panel>
        <PanelHead>
          <PanelTitle>Content Angles</PanelTitle>
          <Counter>{saving ? "Saving…" : `${angles.length} angles`}</Counter>
        </PanelHead>
        {angles.length === 0 && (
          <Muted>No angles yet. Add the recurring themes every post is built from.</Muted>
        )}
        {angles.map((angle, idx) => (
          <div key={idx}>
            <ListRow>
              <strong style={{ fontSize: "var(--font-size-sm)" }}>
                {angle.name || "Untitled angle"}
              </strong>
              <Row>
                <IconButton
                  type="button"
                  onClick={() => setExpandedAngle(expandedAngle === idx ? null : idx)}
                >
                  {expandedAngle === idx ? "Close" : "Edit"}
                </IconButton>
                <IconButton type="button" onClick={() => deleteAngle(idx)}>
                  Delete
                </IconButton>
              </Row>
            </ListRow>
            {expandedAngle === idx && (
              <AngleDetail>
                <FieldStack>
                  <FieldLabel>Name</FieldLabel>
                  <Input
                    value={angle.name}
                    onChange={(e) => updateAngle(idx, { name: e.target.value })}
                    onBlur={commitAngles}
                  />
                </FieldStack>
                <FieldStack>
                  <FieldLabel>Pain</FieldLabel>
                  <Textarea
                    rows={2}
                    value={angle.pain ?? ""}
                    onChange={(e) => updateAngle(idx, { pain: e.target.value })}
                    onBlur={commitAngles}
                  />
                </FieldStack>
                <Grid2>
                  <FieldStack>
                    <FieldLabel>Segment</FieldLabel>
                    <Input
                      value={angle.segment ?? ""}
                      onChange={(e) => updateAngle(idx, { segment: e.target.value })}
                      onBlur={commitAngles}
                    />
                  </FieldStack>
                  <FieldStack>
                    <FieldLabel>Mechanism</FieldLabel>
                    <Input
                      value={angle.mechanism ?? ""}
                      onChange={(e) => updateAngle(idx, { mechanism: e.target.value })}
                      onBlur={commitAngles}
                    />
                  </FieldStack>
                </Grid2>
                <FieldStack>
                  <FieldLabel>Remix from (comma separated)</FieldLabel>
                  <Input
                    value={joinList(angle.remixFrom)}
                    onChange={(e) => updateAngle(idx, { remixFrom: splitList(e.target.value) })}
                    onBlur={commitAngles}
                  />
                </FieldStack>
                <FieldStack>
                  <FieldLabel>Example headlines (comma separated)</FieldLabel>
                  <Input
                    value={joinList(angle.exampleHeadlines)}
                    onChange={(e) =>
                      updateAngle(idx, { exampleHeadlines: splitList(e.target.value) })
                    }
                    onBlur={commitAngles}
                  />
                </FieldStack>
                <FieldStack>
                  <FieldLabel>Why this content</FieldLabel>
                  <Textarea
                    rows={2}
                    value={angle.whyThisContent ?? ""}
                    onChange={(e) => updateAngle(idx, { whyThisContent: e.target.value })}
                    onBlur={commitAngles}
                  />
                </FieldStack>
              </AngleDetail>
            )}
          </div>
        ))}
        <AddRow>
          <Input
            placeholder="New angle, e.g. Tool Stack Chaos"
            value={newAngle}
            onChange={(e) => setNewAngle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addAngle()}
          />
          <Button variant="secondary" onClick={addAngle} disabled={!newAngle.trim()}>
            Add
          </Button>
        </AddRow>
      </Panel>

      {/* ---------------- Tone & Voice ---------------- */}
      <Panel>
        <PanelHead>
          <PanelTitle>Tone &amp; Voice</PanelTitle>
        </PanelHead>
        <PanelSubtitle>
          Rules for how your content copy should sound. These apply to all generated content text.
        </PanelSubtitle>
        <ToneCols>
          <div>
            <PanelHead>
              <FieldLabel>Do&apos;s</FieldLabel>
              <Counter>
                {toneDos.length}/{MAX_TONE}
              </Counter>
            </PanelHead>
            {toneDos.map((d, i) => (
              <ListRow key={i}>
                <span style={{ fontSize: "var(--font-size-sm)" }}>{d}</span>
                <IconButton type="button" onClick={() => removeTone("do", i)}>
                  Remove
                </IconButton>
              </ListRow>
            ))}
            <AddRow>
              <Input
                placeholder="e.g., Use casual, conversational tone"
                value={newDo}
                onChange={(e) => setNewDo(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTone("do")}
                disabled={toneDos.length >= MAX_TONE}
              />
              <Button
                variant="secondary"
                onClick={() => addTone("do")}
                disabled={!newDo.trim() || toneDos.length >= MAX_TONE}
              >
                Add
              </Button>
            </AddRow>
          </div>
          <div>
            <PanelHead>
              <FieldLabel>Don&apos;ts</FieldLabel>
              <Counter>
                {toneDonts.length}/{MAX_TONE}
              </Counter>
            </PanelHead>
            {toneDonts.map((d, i) => (
              <ListRow key={i}>
                <span style={{ fontSize: "var(--font-size-sm)" }}>{d}</span>
                <IconButton type="button" onClick={() => removeTone("dont", i)}>
                  Remove
                </IconButton>
              </ListRow>
            ))}
            <AddRow>
              <Input
                placeholder="e.g., Never use corporate jargon"
                value={newDont}
                onChange={(e) => setNewDont(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTone("dont")}
                disabled={toneDonts.length >= MAX_TONE}
              />
              <Button
                variant="secondary"
                onClick={() => addTone("dont")}
                disabled={!newDont.trim() || toneDonts.length >= MAX_TONE}
              >
                Add
              </Button>
            </AddRow>
          </div>
        </ToneCols>
      </Panel>

      {/* ---------------- Identity & Purpose ---------------- */}
      <Grid2>
        <Panel>
          <PanelHead>
            <PanelTitle>Identity &amp; Product</PanelTitle>
            {editIdentity ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  persist({ identity });
                  setEditIdentity(false);
                }}
              >
                Save
              </Button>
            ) : (
              <IconButton type="button" onClick={() => setEditIdentity(true)}>
                Edit
              </IconButton>
            )}
          </PanelHead>
          {(
            [
              ["Core Identity", "coreIdentity"],
              ["Product Offering", "productOffering"],
              ["Unique Benefits", "uniqueBenefits"],
              ["Problem Solution", "problemSolution"],
            ] as [string, keyof IdentityAndProduct][]
          ).map(([label, key]) => (
            <FieldStack key={key}>
              <FieldLabel>{label}</FieldLabel>
              {editIdentity ? (
                <Textarea
                  rows={3}
                  value={identity[key] ?? ""}
                  onChange={(e) => setIdentity({ ...identity, [key]: e.target.value })}
                />
              ) : (
                <FieldValue>{identity[key] || <Muted as="span">Not set</Muted>}</FieldValue>
              )}
            </FieldStack>
          ))}
        </Panel>

        <Panel>
          <PanelHead>
            <PanelTitle>Purpose &amp; Positioning</PanelTitle>
            {editPurpose ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  persist({ purpose });
                  setEditPurpose(false);
                }}
              >
                Save
              </Button>
            ) : (
              <IconButton type="button" onClick={() => setEditPurpose(true)}>
                Edit
              </IconButton>
            )}
          </PanelHead>
          {(
            [
              ["Mission", "mission"],
              ["Differentiation", "differentiation"],
            ] as [string, keyof PurposeAndPositioning][]
          ).map(([label, key]) => (
            <FieldStack key={key}>
              <FieldLabel>{label}</FieldLabel>
              {editPurpose ? (
                <Textarea
                  rows={3}
                  value={purpose[key] ?? ""}
                  onChange={(e) => setPurpose({ ...purpose, [key]: e.target.value })}
                />
              ) : (
                <FieldValue>{purpose[key] || <Muted as="span">Not set</Muted>}</FieldValue>
              )}
            </FieldStack>
          ))}
          <FieldStack>
            <FieldLabel>Owned Space</FieldLabel>
            {editPurpose ? (
              <Textarea
                rows={3}
                value={purpose.ownedSpace ?? ""}
                onChange={(e) => setPurpose({ ...purpose, ownedSpace: e.target.value })}
              />
            ) : purpose.ownedSpace ? (
              <OwnedSpaceBox>{purpose.ownedSpace}</OwnedSpaceBox>
            ) : (
              <FieldValue>
                <Muted as="span">Not set</Muted>
              </FieldValue>
            )}
          </FieldStack>
        </Panel>
      </Grid2>

      {/* ---------------- Market & Competition ---------------- */}
      <Panel>
        <PanelHead>
          <PanelTitle>Market &amp; Competition</PanelTitle>
          {editMarket ? (
            <Button variant="secondary" size="sm" onClick={saveMarket}>
              Save
            </Button>
          ) : (
            <IconButton type="button" onClick={() => setEditMarket(true)}>
              Edit
            </IconButton>
          )}
        </PanelHead>
        <Grid2>
          <div>
            <FieldLabel>Customer Segments</FieldLabel>
            <SegBar />
            {segments.map((seg, i) => (
              <ListRow key={i}>
                {editMarket ? (
                  <Row style={{ flex: 1 }}>
                    <Input
                      placeholder="Segment"
                      value={seg.label}
                      onChange={(e) =>
                        setSegments(
                          segments.map((s, j) =>
                            j === i ? { ...s, label: e.target.value } : s
                          )
                        )
                      }
                    />
                    <Input
                      type="number"
                      placeholder="%"
                      value={seg.percent ?? ""}
                      onChange={(e) =>
                        setSegments(
                          segments.map((s, j) =>
                            j === i
                              ? { ...s, percent: Number(e.target.value) || undefined }
                              : s
                          )
                        )
                      }
                      style={{ maxWidth: 90 }}
                    />
                    <IconButton
                      type="button"
                      onClick={() => setSegments(segments.filter((_, j) => j !== i))}
                    >
                      Delete
                    </IconButton>
                  </Row>
                ) : (
                  <>
                    <span style={{ fontSize: "var(--font-size-sm)" }}>{seg.label}</span>
                    <Tag variant="neutral" size="sm">
                      {seg.percent != null ? `${seg.percent}%` : "—"}
                    </Tag>
                  </>
                )}
              </ListRow>
            ))}
            {editMarket && (
              <AddRow>
                <Button variant="ghost" size="sm" onClick={addSegment}>
                  + Add segment
                </Button>
              </AddRow>
            )}
          </div>
          <div>
            <FieldLabel>Competitors</FieldLabel>
            <div style={{ height: 6, marginBottom: "var(--space-3)" }} />
            {!editMarket ? (
              <Chips>
                {competitors.length === 0 && <Muted>None added</Muted>}
                {competitors.map((c, i) => (
                  <Tag key={i} variant="neutral" size="md">
                    {c.label}
                  </Tag>
                ))}
              </Chips>
            ) : (
              <>
                {competitors.map((c, i) => (
                  <ListRow key={i}>
                    <Row style={{ flex: 1 }}>
                      <Input
                        placeholder="Name"
                        value={c.label}
                        onChange={(e) =>
                          setCompetitors(
                            competitors.map((x, j) =>
                              j === i ? { ...x, label: e.target.value } : x
                            )
                          )
                        }
                      />
                      <Input
                        placeholder="https://"
                        value={c.url ?? ""}
                        onChange={(e) =>
                          setCompetitors(
                            competitors.map((x, j) =>
                              j === i ? { ...x, url: e.target.value } : x
                            )
                          )
                        }
                      />
                      <IconButton
                        type="button"
                        onClick={() =>
                          setCompetitors(competitors.filter((_, j) => j !== i))
                        }
                      >
                        Delete
                      </IconButton>
                    </Row>
                  </ListRow>
                ))}
                <AddRow>
                  <Button variant="ghost" size="sm" onClick={addCompetitor}>
                    + Add competitor
                  </Button>
                </AddRow>
              </>
            )}
          </div>
        </Grid2>
      </Panel>
    </Wrap>
  );
};

export default BrandForm;
